/**
 * ULID (Universally Unique Lexicographically Sortable Identifier) implementation.
 *
 * ULIDs are sortable by generation time and encoded in Crockford's Base32 format.
 * Format: 01ARZ3NDEKTSV4RRFFQ69G5FAV (26 characters)
 * - Timestamp: first 10 characters (48 bits) - valid until 10889 AD
 * - Randomness: last 16 characters (80 bits)
 *
 * This implementation creates ULID-compatible strings without external dependencies.
 */

// Crockford's Base32 alphabet (0-9, A-Z excluding I, L, O, U)
const ENCODING = '0123456789ABCDEFGHJKMNPQRSTVWXYZ';

/**
 * Generate a unique, sortable ULID.
 * ULIDs are lexicographically sortable by generation time.
 */
export function generateULID(): string {
  // Get current timestamp in milliseconds since Unix epoch
  // ULID uses 48-bit timestamp (valid until 10889 AD)
  const timestamp = Date.now();

  // Encode timestamp (48 bits = 10 base32 characters)
  let timestampPart = '';
  for (let i = 9; i >= 0; i--) {
    const shift = i * 5;
    const index = (timestamp >> shift) & 0x1f;
    timestampPart = ENCODING[index] + timestampPart;
  }

  // Generate random part (80 bits = 16 base32 characters)
  let randomPart = '';
  const randomValues = new Uint8Array(10);
  crypto.getRandomValues(randomValues);

  for (let i = 0; i < 10; i++) {
    const byte1 = randomValues[i];
    const byte2 = randomValues[i] >> 5;

    randomPart += ENCODING[byte1 & 0x1f];
    randomPart += ENCODING[byte2];
  }

  return timestampPart + randomPart;
}

/**
 * Generate a unique ID. Alias for generateULID for backward compatibility.
 * ULIDs are sortable lexicographically by generation time,
 * making them ideal for database keys and sorting.
 */
export function generateId(): string {
  return generateULID();
}
